// Curated, named icon list for the DB-driven global toolbar
// (global_menu_buttons.icon_key). These are the exact inline SVGs the
// toolbar already used before it became config-driven — kept here as a
// single named source so the seeded buttons render pixel-identical to the
// old hard-coded markup, and so the admin modal can offer a "pick from a
// list" selector with a live preview, per the user's request. A button can
// instead reference an uploaded image via `icon_path`, which always wins
// over `icon_key` when both are set (see global_menu_renderer.js).
const GLOBAL_MENU_ICONS = Object.freeze([
  {
    key: 'undo',
    label: 'Rückgängig',
    svgMarkup: '<path d="M9 7 4 12l5 5" /><path d="M5 12h9a6 6 0 0 1 6 6v1" />'
  },
  {
    key: 'redo',
    label: 'Vorwärts',
    svgMarkup: '<path d="m15 7 5 5-5 5" /><path d="M19 12h-9a6 6 0 0 0-6 6v1" />'
  },
  {
    key: 'new-order',
    label: 'Neuer Auftrag',
    svgMarkup: '<path d="M6 3h8l4 4v14H6z" /><path d="M14 3v5h4" /><path d="M12 12v6" /><path d="M9 15h6" />'
  },
  {
    key: 'save',
    label: 'Speichern',
    svgMarkup: '<path d="M5 4h12l2 2v14H5z" /><path d="M8 4v6h8V4" /><path d="M8 20v-6h8v6" />'
  },
  {
    key: 'save-as',
    label: 'Speichern unter',
    svgMarkup: '<path d="M5 4h11l3 3v13H5z" /><path d="M8 4v6h7V4" /><path d="M8 20v-5h5" /><path d="M14 17.5 19.5 12l2.5 2.5L16.5 20H14z" />'
  },
  {
    key: 'pdf',
    label: 'PDF laden',
    svgMarkup: '<path d="M6 3h8l4 4v14H6z" /><path d="M14 3v5h5" /><path d="M8 17h8" /><path d="M8 13h8" />'
  },
  {
    key: 'cad-file',
    label: 'DXF / DWG laden',
    svgMarkup: '<path d="M6 3h8l4 4v14H6z" /><path d="M14 3v5h4" /><path d="m8.5 17.5 6.8-6.8 2 2-6.8 6.8H8.5z" /><path d="m14.4 11.6 2 2" />'
  },
  // Eigener Key statt Wiederverwendung von 'pdf' (das ist an den unabhaengigen
  // "PDF laden"-Button/fileFloorplan gekoppelt) -- gleiche Dokument-Optik,
  // fuers Infos-System (Werkzeugleisten-Button + BOM-Spalte), siehe
  // project_infos_system_20260810.md.
  {
    key: 'pdf-doc',
    label: 'Informationen (PDF)',
    svgMarkup: '<path d="M6 3h8l4 4v14H6z" /><path d="M14 3v5h5" /><path d="M8 17h8" /><path d="M8 13h8" /><path d="M8 9h3" />'
  },
  {
    key: 'globe',
    label: 'Satellit',
    svgMarkup: '<circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.4 2.6 3.6 5.6 3.6 9S14.4 18.4 12 21" /><path d="M12 3c-2.4 2.6-3.6 5.6-3.6 9S9.6 18.4 12 21" />'
  },
  {
    key: 'floorplan',
    label: 'Grundrissplan',
    svgMarkup: '<path d="M4 4h16v16H4z" /><path d="M4 10h7V4" /><path d="M11 20v-6h9" /><path d="M7 10v4h4" /><path d="M15 7h5" />'
  },
  {
    key: 'delete',
    label: 'Löschen',
    svgMarkup: '<path d="M6 6l12 12" /><path d="M18 6L6 18" />'
  },
  {
    key: 'dimension',
    label: 'Bemassung',
    svgMarkup: '<path d="M4 17l13-13 3 3L7 20z" /><path d="M8 16l-1.5-1.5" /><path d="M11 13l-1.5-1.5" /><path d="M14 10l-1.5-1.5" /><path d="M17 7l-1.5-1.5" />'
  },
  {
    key: 'angular-dimension',
    label: 'Winkelbemassung',
    svgMarkup: '<path d="M20 19H4L15 4" /><path d="M12 19a8 8 0 0 0-3.3-6.5" />'
  },
  {
    key: 'area-measurement',
    label: 'Flächenmessung',
    svgMarkup: '<rect x="4" y="6" width="16" height="12" rx="1" />'
  },
  {
    key: 'auto-area-measurement',
    label: 'Automatische Flächenmessung',
    svgMarkup: '<rect x="3" y="5" width="18" height="14" rx="1" /><path d="M12 8.2c.35 2.25 1.55 3.45 3.8 3.8-2.25.35-3.45 1.55-3.8 3.8-.35-2.25-1.55-3.45-3.8-3.8 2.25-.35 3.45-1.55 3.8-3.8Z" />'
  },
  {
    key: 'obstacle-pipe',
    label: 'Rohr',
    svgMarkup: '<ellipse cx="7" cy="12" rx="3.5" ry="6" /><path d="M7 6h10" /><path d="M7 18h10" /><ellipse cx="17" cy="12" rx="3.5" ry="6" /><path d="M17 9.2a1.8 2.8 0 0 1 0 5.6" />'
  },
  {
    key: 'obstacle-duct',
    label: 'Lüftung',
    svgMarkup: '<path d="M5 8h11l3 3v7H8l-3-3z" /><path d="M5 8l3 3h11" /><path d="M8 11v7" /><path d="M11 11v7" /><path d="M14 11v7" />'
  },
  {
    key: 'sketch-wall',
    label: 'Wand (parametrisch)',
    svgMarkup: '<path d="M4 7h16v12H4z" /><path d="M4 11h16" /><path d="M4 15h16" /><path d="M8 7v4" /><path d="M16 7v4" /><path d="M12 11v4" /><path d="M8 15v4" /><path d="M16 15v4" />'
  },
  {
    key: 'print',
    label: 'Drucken',
    svgMarkup: '<path d="M6 9V4h12v5" /><rect x="4" y="9" width="16" height="8" rx="1.5" /><path d="M8 14h8v6H8z" />'
  },
  // Not an SVG in the original markup — the text-annotation button was
  // always just a bold "A" glyph, styled (bold, dark) via the exact same
  // `.icon-btn.<key>-icon` CSS convention as every real SVG icon here (see
  // styles.css). Kept in this same list so it's just as pickable/rendered
  // as any other icon, with `glyph` instead of `svgMarkup`.
  {
    key: 'text-annotation',
    label: 'Textfeld setzen',
    glyph: 'A'
  }
]);

function globalMenuIconByKey(key) {
  if (!key) return null;
  return GLOBAL_MENU_ICONS.find(icon => icon.key === key) || null;
}

// Inner markup only (no wrapping element) — svg for a real icon, or a plain
// glyph span for something like text-annotation's "A". Callers add their
// own `<button>`/preview wrapper and the `<icon_key>-icon` CSS class that
// actually colors this (see global_menu_renderer.js / toolbar_admin_modal.js).
function globalMenuIconContentHtml(key) {
  const icon = globalMenuIconByKey(key);
  if (!icon) return '';
  if (icon.svgMarkup) return `<svg viewBox="0 0 24 24" aria-hidden="true">${icon.svgMarkup}</svg>`;
  if (icon.glyph) return `<span aria-hidden="true">${icon.glyph}</span>`;
  return '';
}

export {
  GLOBAL_MENU_ICONS,
  globalMenuIconByKey,
  globalMenuIconContentHtml
};
